import type { PipelineRunResult, Project } from '@enersu/core-orchestrator';
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';

export type GenericAnalysisStatus = 'not-run' | 'complete' | 'needs-review' | 'error';

export interface GenericAnalysisMetric {
  label: string;
  value: string | number;
  unit?: string;
}

export interface GenericAnalysisOutput {
  id: string;
  title: string;
  status: GenericAnalysisStatus;
  summary?: string;
  metrics?: readonly GenericAnalysisMetric[];
  notes?: readonly string[];
}

export interface HebrewReportMessages {
  report: {
    title: string;
    subtitle: string;
    disclaimer: string;
  };
  sections: {
    summary: string;
    analyses: string;
  };
  labels: {
    project: string;
    municipality: string;
    generatedAt: string;
    timezone: string;
    buildings: string;
    status: string;
    metrics: string;
    notes: string;
  };
  statuses: Record<GenericAnalysisStatus, string>;
  emptyState: {
    analyses: string;
  };
}

export interface BuildReportShellInput {
  project: Project;
  generatedAt: Date;
  analyses: readonly GenericAnalysisOutput[];
  messages: HebrewReportMessages;
}

export function pipelineResultsToAnalyses(
  result: PipelineRunResult<GenericAnalysisOutput>,
): GenericAnalysisOutput[] {
  return result.results.map((entry) => {
    if (entry.status === 'fulfilled') {
      return entry.value;
    }

    return {
      id: entry.id,
      title: entry.id,
      status: 'error',
      summary: normalizeError(entry.error),
    };
  });
}

export function buildHebrewReportShell(input: BuildReportShellInput): TDocumentDefinitions {
  const { project, generatedAt, analyses, messages } = input;
  const analysisContent =
    analyses.length === 0
      ? [{ text: messages.emptyState.analyses, style: 'body' }]
      : analyses.map((analysis) => buildAnalysisSection(analysis, messages));

  return {
    pageSize: 'A4',
    pageMargins: [40, 48, 40, 56],
    info: {
      title: messages.report.title,
      author: 'EnerSU',
      subject: 'Municipal compliance report shell',
      creator: 'EnerSU Forma plugin',
      creationDate: generatedAt,
    },
    defaultStyle: {
      font: 'Heebo',
      fontSize: 10,
      lineHeight: 1.25,
      alignment: 'right',
    },
    styles: {
      title: { fontSize: 20, bold: true, margin: [0, 0, 0, 4] },
      subtitle: { fontSize: 11, color: '#475569', margin: [0, 0, 0, 18] },
      sectionTitle: { fontSize: 14, bold: true, margin: [0, 14, 0, 8] },
      analysisTitle: { fontSize: 12, bold: true, margin: [0, 0, 0, 4] },
      metadata: { color: '#334155', margin: [0, 0, 0, 3] },
      body: { margin: [0, 0, 0, 6] },
      smallHeading: { bold: true, margin: [0, 4, 0, 2] },
      disclaimer: { color: '#64748b', fontSize: 8, margin: [0, 18, 0, 0] },
    },
    content: [
      { text: messages.report.title, style: 'title' },
      { text: messages.report.subtitle, style: 'subtitle' },
      { text: messages.sections.summary, style: 'sectionTitle' },
      {
        stack: [
          { text: `${messages.labels.project}: ${project.name}`, style: 'metadata' },
          {
            text: `${messages.labels.municipality}: ${project.municipalityId}`,
            style: 'metadata',
          },
          { text: `${messages.labels.timezone}: ${project.timezone}`, style: 'metadata' },
          {
            text: `${messages.labels.generatedAt}: ${generatedAt.toISOString()}`,
            style: 'metadata',
          },
          {
            text: `${messages.labels.buildings}: ${project.buildings.length}`,
            style: 'metadata',
          },
        ],
      },
      { text: messages.sections.analyses, style: 'sectionTitle' },
      ...analysisContent,
      { text: messages.report.disclaimer, style: 'disclaimer' },
    ],
  };
}

function buildAnalysisSection(
  analysis: GenericAnalysisOutput,
  messages: HebrewReportMessages,
): Content {
  const stack: Content[] = [
    { text: analysis.title, style: 'analysisTitle' },
    {
      text: `${messages.labels.status}: ${messages.statuses[analysis.status]}`,
      style: 'metadata',
    },
  ];

  if (analysis.summary !== undefined) {
    stack.push({ text: analysis.summary, style: 'body' });
  }

  if (analysis.metrics !== undefined && analysis.metrics.length > 0) {
    stack.push({ text: messages.labels.metrics, style: 'smallHeading' });
    stack.push({ ul: analysis.metrics.map(formatMetric), style: 'body' });
  }

  if (analysis.notes !== undefined && analysis.notes.length > 0) {
    stack.push({ text: messages.labels.notes, style: 'smallHeading' });
    stack.push({ ul: [...analysis.notes], style: 'body' });
  }

  return { stack, margin: [0, 0, 0, 12] };
}

function formatMetric(metric: GenericAnalysisMetric): string {
  return metric.unit === undefined
    ? `${metric.label}: ${metric.value}`
    : `${metric.label}: ${metric.value} ${metric.unit}`;
}

function normalizeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
